import { Heart, Users, Award, Clock } from 'lucide-react';
import { motion } from 'framer-motion';

const stats = [
  { icon: Heart, value: '100%', label: 'Loving Care', color: 'primary', shape: 'rounded-full' },
  { icon: Users, value: '1-10', label: 'Age Range', color: 'secondary', shape: 'rounded-[2rem]' },
  { icon: Award, value: 'Premium', label: 'Education', color: 'accent', shape: 'rounded-[2.5rem]' },
  { icon: Clock, value: '7AM-8PM', label: 'Extended Hours', color: 'success', shape: 'rounded-[2rem]' },
];

const cardVariants = {
  hidden: { opacity: 0, y: 25 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const AboutSection = () => {
  return (
    <section id="about" className="py-6 md:py-24 bg-background relative" data-testid="about-section">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto space-y-8 md:space-y-16">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
            className="text-center space-y-4"
          >
            <span className="inline-block bg-secondary/15 text-secondary-foreground text-sm font-bold px-5 py-2 rounded-full tracking-wide uppercase">
              About Us
            </span>
            <h2 className="font-fredoka font-bold text-4xl sm:text-5xl text-foreground" data-testid="about-heading">
              Welcome to Hooyo's Place
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              At Hooyo's Place, we believe every child deserves a nurturing environment where they can learn, play, and grow. Our early education and childcare home provides a safe, loving space that feels like a second home.
            </p>
          </motion.div>

          {/* Bento Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  custom={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-30px" }}
                  variants={cardVariants}
                  whileHover={{ scale: 1.04, y: -5 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                  className={`bg-${stat.color}/8 border-2 border-${stat.color}/15 ${stat.shape} p-6 md:p-8 text-center space-y-3 cursor-default`}
                  data-testid={`about-stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className="flex justify-center">
                    <div className={`w-12 h-12 rounded-full bg-${stat.color}/15 flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 text-${stat.color}`} />
                    </div>
                  </div>
                  <div className={`font-fredoka font-bold text-2xl md:text-3xl text-${stat.color}`}>
                    {stat.value}
                  </div>
                  <div className="text-sm text-muted-foreground font-semibold">
                    {stat.label}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Mission Statement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-soft border border-border/50 text-center"
            data-testid="about-mission"
          >
            <h3 className="font-fredoka font-bold text-2xl sm:text-3xl text-foreground mb-4">
              Our Mission
            </h3>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto">
              We are committed to providing exceptional early childhood education and care that nurtures each child's unique potential. Through engaging activities, experienced caregivers, and a warm, inclusive environment, we help children develop the skills, confidence, and love of learning that will serve them throughout their lives.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
